/* Altara PROLOGUE · v2 · matches spoken script (~50s)
 * Beat 1 · Question we were given (5s)
 * Beat 2 · The alternatives (8s)
 * Beat 3 · The burden (6s)
 * Beat 4 · The different question (6s)
 * Beat 5 · The insight — CONDUCT problem (7s)
 * Beat 6 · The trust layer (8s)
 * Beat 7 · Upstream + aligned incentives (10s) */
import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { WalletShell, NAVY, GOLD } from "@/components/wallet/WalletShell";
import { Play, RotateCcw, Search, MessageSquare, Eye, User, Building2, ArrowRight, HelpCircle } from "lucide-react";

const CREAM = "#F5EFE6";

/* ---------- BEATS ---------- */
const BEATS = [
  { id: "question",     kind: "text",  duration: 5000,  Comp: BeatQuestion },
  { id: "alternatives", kind: "text",  duration: 8000,  Comp: BeatAlternatives },
  { id: "burden",       kind: "image", duration: 6000,  src: "/prologue/slide3_burden.png",
    kenBurns: { from: { scale: 1.02, x: 0, y: 0 }, to: { scale: 1.08, x: -1, y: -1 } } },
  { id: "different",    kind: "text",  duration: 6000,  Comp: BeatDifferent },
  { id: "insight",      kind: "text",  duration: 7000,  Comp: BeatInsight },
  { id: "layer",        kind: "image", duration: 8000,  src: "/prologue/slide5_layer.png",
    kenBurns: { from: { scale: 1.0, x: 0, y: 0 }, to: { scale: 1.05, x: 0, y: -1 } } },
  { id: "upstream",     kind: "text",  duration: 10000, Comp: BeatUpstream },
];
const TOTAL = BEATS.reduce((a, b) => a + b.duration, 0);

/* ---------- PAGE SHELL ---------- */
export default function WalletAltaraPrologueDemo() {
  const [params] = useSearchParams();
  const embed = params.get("embed") === "1";
  const autoplay = params.get("autoplay") !== "0";
  if (embed) return (
    <div className="w-screen h-screen relative overflow-hidden bg-black" data-testid="altara-prologue-embed">
      <Player autoplay={autoplay} />
    </div>
  );
  return (
    <WalletShell activeTab="demos">
      <div className="max-w-6xl mx-auto px-4 py-8" data-testid="altara-prologue-demo">
        <div className="text-center mb-4">
          <div className="text-[10px] uppercase tracking-[0.3em] font-semibold" style={{ color: GOLD }}>Slide asset</div>
          <h1 className="font-display text-3xl font-semibold" style={{ color: NAVY }}>Prologue · v2</h1>
        </div>
        <div className="relative rounded-3xl overflow-hidden shadow-2xl bg-black" style={{ aspectRatio: "16/9" }}>
          <Player autoplay />
        </div>
      </div>
    </WalletShell>
  );
}

/* ---------- PLAYER ---------- */
function Player({ autoplay }) {
  const [beatIdx, setBeatIdx] = useState(-1);
  const [elapsed, setElapsed] = useState(0);
  const [done, setDone] = useState(false);
  const raf = useRef(null); const start = useRef(0); const fired = useRef(false);

  const play = () => {
    setBeatIdx(0); setElapsed(0); setDone(false);
    start.current = performance.now();
    const tick = (now) => {
      const t = now - start.current;
      setElapsed(t);
      if (t >= TOTAL) { setDone(true); setBeatIdx(BEATS.length - 1); return; }
      let acc = 0;
      for (let i = 0; i < BEATS.length; i++) {
        acc += BEATS[i].duration;
        if (t < acc) { setBeatIdx(i); break; }
      }
      raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
  };
  const stop = () => { if (raf.current) cancelAnimationFrame(raf.current); raf.current = null; };
  const reset = () => { stop(); setBeatIdx(-1); setElapsed(0); setDone(false); };

  useEffect(() => {
    if (!autoplay || fired.current) return;
    fired.current = true;
    setTimeout(play, 700);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoplay]);
  useEffect(() => () => stop(), []);

  const beat = beatIdx >= 0 ? BEATS[beatIdx] : null;
  const beatStart = useMemo(() => BEATS.slice(0, beatIdx).reduce((a, b) => a + b.duration, 0), [beatIdx]);
  const inBeatT = Math.max(0, elapsed - beatStart);

  return (
    <>
      {/* Idle overlay */}
      {beatIdx < 0 && (
        <div className="absolute inset-0 flex items-center justify-center" style={{
          background: `radial-gradient(1200px 800px at 50% 40%, #142958 0%, #0A1F44 50%, #05132C 100%)` }}>
          <button onClick={play} data-testid="play-prologue"
            className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold"
            style={{ background: GOLD, color: NAVY }}>
            <Play className="w-4 h-4" /> Play prologue
          </button>
        </div>
      )}

      {/* Beat renderer */}
      <AnimatePresence mode="wait">
        {beat && (
          <motion.div key={beat.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="absolute inset-0">
            {beat.kind === "image"
              ? <ImageBeat src={beat.src} kb={beat.kenBurns} duration={beat.duration} />
              : <beat.Comp t={inBeatT} duration={beat.duration} />}
          </motion.div>
        )}
      </AnimatePresence>

      {/* HUD */}
      {beatIdx >= 0 && (
        <div className="absolute top-4 left-4 right-4 flex items-center justify-between z-30 pointer-events-none">
          <div className="text-[10px] uppercase tracking-[0.32em] font-semibold px-3 py-1 rounded-full bg-black/40 text-white/80 backdrop-blur">
            Prologue · why Altara exists
          </div>
          <div className="text-[11px] font-mono text-white/70 tabular-nums px-2 py-1 rounded bg-black/40 backdrop-blur">
            {beatIdx + 1}/{BEATS.length} · {formatMs(elapsed)}
          </div>
        </div>
      )}
      {beatIdx >= 0 && (
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-black/30 z-30 pointer-events-none">
          <motion.div className="h-full" style={{ background: GOLD }}
            animate={{ width: `${Math.min(100, (elapsed / TOTAL) * 100)}%` }} transition={{ ease: "linear", duration: 0 }} />
        </div>
      )}
      {beatIdx >= 0 && (
        <button onClick={reset} data-testid="reset-prologue"
          className="absolute bottom-4 left-4 w-8 h-8 rounded-full bg-white/10 border border-white/20 flex items-center justify-center z-30">
          <RotateCcw className="w-3.5 h-3.5 text-white/80" />
        </button>
      )}
    </>
  );
}

/* ---------- IMAGE BEAT (Ken-Burns) ---------- */
function ImageBeat({ src, kb, duration }) {
  return (
    <motion.div className="absolute inset-0"
      initial={{ scale: kb.from.scale, x: `${kb.from.x}%`, y: `${kb.from.y}%` }}
      animate={{ scale: kb.to.scale, x: `${kb.to.x}%`, y: `${kb.to.y}%` }}
      transition={{ duration: duration / 1000, ease: "linear" }}>
      <img src={src} alt="" className="w-full h-full object-cover" />
    </motion.div>
  );
}

/* ---------- BEAT 1 · The question ---------- */
function BeatQuestion() {
  return (
    <div className="w-full h-full flex items-center justify-center px-14"
         style={{ background: CREAM }}>
      <div className="max-w-4xl">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}
                    className="flex items-center gap-2 text-[10px] uppercase tracking-[0.32em] font-semibold" style={{ color: GOLD }}>
          <HelpCircle className="w-3.5 h-3.5" /> The IOSCO TechSprint 2026 question
        </motion.div>
        <motion.h1 initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4, duration: 0.8 }}
                   className="font-display font-semibold mt-4 leading-tight"
                   style={{ color: NAVY, fontSize: 44, letterSpacing: "-0.02em" }}>
          How can technology help retail investors build skills and knowledge to better <em style={{ color: GOLD, fontStyle: "italic" }}>identify and avoid</em> AI-enabled fraud and scams?
        </motion.h1>
      </div>
    </div>
  );
}

/* ---------- BEAT 2 · The alternatives ---------- */
function BeatAlternatives() {
  const alts = [
    { Icon: Search,        label: "Suspicious-link checkers" },
    { Icon: MessageSquare, label: "Message analysers" },
    { Icon: Eye,           label: "Deepfake detectors" },
  ];
  return (
    <div className="w-full h-full flex items-center justify-center px-14" style={{ background: CREAM }}>
      <div className="max-w-5xl w-full">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                    className="text-[10px] uppercase tracking-[0.32em] font-semibold text-center" style={{ color: GOLD }}>
          Many valid interventions we could have built
        </motion.div>
        <div className="mt-8 grid grid-cols-3 gap-5">
          {alts.map((a, i) => (
            <motion.div key={a.label} initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 + i * 0.45, duration: 0.5 }}
                        className="rounded-2xl p-6 border text-center bg-white shadow-sm"
                        style={{ borderColor: "#E2E8F0" }}>
              <div className="w-11 h-11 rounded-2xl mx-auto flex items-center justify-center" style={{ background: `${GOLD}22` }}>
                <a.Icon className="w-5 h-5" style={{ color: NAVY }} />
              </div>
              <div className="mt-3 font-display font-semibold text-lg" style={{ color: NAVY }}>{a.label}</div>
            </motion.div>
          ))}
        </div>
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 2.4, duration: 0.6 }}
                    className="mt-10 text-center">
          <div className="text-[11px] uppercase tracking-[0.28em] font-semibold" style={{ color: GOLD }}>But…</div>
          <div className="font-display text-3xl font-semibold mt-2 leading-snug" style={{ color: NAVY }}>
            All of them place the <span style={{ color: GOLD }}>burden</span> back on the consumer.
          </div>
        </motion.div>
      </div>
    </div>
  );
}

/* ---------- BEAT 4 · The different question ---------- */
function BeatDifferent() {
  return (
    <div className="w-full h-full flex items-center justify-center text-center px-14"
         style={{ background: `radial-gradient(900px 600px at 50% 45%, #142958 0%, #0A1F44 50%, #05132C 100%)` }}>
      <div>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}
                    className="text-[10px] uppercase tracking-[0.32em] font-semibold" style={{ color: GOLD }}>
          So we asked
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4, duration: 0.7 }}
                    className="font-display font-semibold mt-4 text-white"
                    style={{ fontSize: 56, letterSpacing: "-0.02em", lineHeight: 1.05 }}>
          What if we asked a <span style={{ color: GOLD }}>different</span> question?
        </motion.div>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.8, duration: 0.7 }}
                    className="mt-6 text-white/80 text-xl max-w-3xl mx-auto leading-relaxed">
          Instead of teaching consumers how to identify what&apos;s <em>fake</em>,<br />
          make it easier for them to recognise what <span style={{ color: GOLD }}>can be trusted</span>.
        </motion.div>
      </div>
    </div>
  );
}

/* ---------- BEAT 5 · The insight — CONDUCT problem ---------- */
function BeatInsight() {
  return (
    <div className="w-full h-full flex items-center justify-center px-14"
         style={{ background: `radial-gradient(900px 600px at 50% 45%, #142958 0%, #0A1F44 50%, #05132C 100%)` }}>
      <div className="max-w-5xl w-full text-center">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}
                    className="text-[10px] uppercase tracking-[0.32em] font-semibold" style={{ color: GOLD }}>
          What we realised
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.6 }}
                    className="font-display font-semibold mt-4 text-white/80"
                    style={{ fontSize: 40 }}>
          Preventing consumer harm isn&apos;t only a fraud problem.
        </motion.div>
        <motion.div initial={{ opacity: 0, scale: 0.94, y: 14 }} animate={{ opacity: 1, scale: 1, y: 0 }}
                    transition={{ delay: 1.4, duration: 0.7 }}
                    className="font-display font-semibold mt-2 leading-tight"
                    style={{ fontSize: 68, letterSpacing: "-0.02em", color: GOLD }}>
          It is a <span style={{ textDecoration: "underline", textDecorationThickness: 4, textUnderlineOffset: 8 }}>conduct</span> problem.
        </motion.div>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 3.0, duration: 0.7 }}
                    className="mt-6 text-white/70 text-lg max-w-3xl mx-auto leading-relaxed">
          Institutions already have responsibilities around fair treatment, product design, and disclosure.
          What if fair conduct itself became <em>preventative infrastructure</em>?
        </motion.div>
      </div>
    </div>
  );
}

/* ---------- BEAT 7 · Upstream + aligned incentives ---------- */
function BeatUpstream() {
  return (
    <div className="w-full h-full flex items-center justify-center px-14"
         style={{ background: `radial-gradient(900px 600px at 50% 40%, #142958 0%, #0A1F44 50%, #05132C 100%)` }}>
      <div className="max-w-6xl w-full">
        <div className="text-center">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}
                      className="text-[10px] uppercase tracking-[0.32em] font-semibold" style={{ color: GOLD }}>
            The upstream shift
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.6 }}
                      className="font-display mt-3 text-white/80" style={{ fontSize: 22 }}>
            To change consumer outcomes at scale,<br />we can&apos;t depend on millions downloading an app.
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 14, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }}
                      transition={{ delay: 1.4, duration: 0.7 }}
                      className="font-display font-semibold mt-4"
                      style={{ fontSize: 66, letterSpacing: "-0.02em", color: "white", lineHeight: 1.02 }}>
            We change what happens <span style={{ color: GOLD }}>upstream</span>.
          </motion.div>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 3.0, duration: 0.6 }}
                      className="mt-5 text-white/85 text-lg max-w-3xl mx-auto">
            The institution participates in <em>establishing</em> the trust.
          </motion.div>
        </div>
        <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 4.6, duration: 0.7 }}
                    className="grid grid-cols-2 gap-4 mt-8">
          <div className="rounded-2xl p-5 border" style={{ background: "rgba(255,255,255,0.04)", borderColor: `${GOLD}55` }}>
            <div className="flex items-center gap-2 text-[10px] tracking-[0.24em] uppercase font-semibold" style={{ color: GOLD }}>
              <User className="w-3.5 h-3.5" /> For the investor
            </div>
            <div className="font-display text-xl font-semibold text-white mt-1.5 leading-snug">
              Verifiable protection.
            </div>
            <div className="text-[13px] text-white/70 mt-1">Trust is portable, evidenced, and hers.</div>
          </div>
          <div className="rounded-2xl p-5 border" style={{ background: "rgba(255,255,255,0.04)", borderColor: `${GOLD}55` }}>
            <div className="flex items-center gap-2 text-[10px] tracking-[0.24em] uppercase font-semibold" style={{ color: GOLD }}>
              <Building2 className="w-3.5 h-3.5" /> For the institution
            </div>
            <div className="font-display text-xl font-semibold text-white mt-1.5 leading-snug">
              Verifiable conduct.
            </div>
            <div className="text-[13px] text-white/70 mt-1">Every act of support becomes recorded evidence.</div>
          </div>
        </motion.div>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 6.6, duration: 0.7 }}
                    className="mt-6 flex items-center justify-center gap-3 text-white/90">
          <div className="text-lg font-display font-semibold">One infrastructure.</div>
          <ArrowRight className="w-4 h-4" style={{ color: GOLD }} />
          <div className="text-lg font-display font-semibold" style={{ color: GOLD }}>Two aligned incentives.</div>
        </motion.div>
      </div>
    </div>
  );
}

function formatMs(ms) {
  const s = Math.floor(ms / 1000);
  return `0:${s.toString().padStart(2, "0")}`;
}
